import * as React from "react";

/**
 * `lxContext` is a util to create a React Context and
 * a hook for application wide state management.
 *
 *
 * ### Usage:
 *
 * Create a file that holds the context (state and logic) you want to share and use
 * across multiple components.
 *
 * ```js
 * // file: my-view-context.ts
 * function useMyViewContextFactory() {
 *   const [counter, setCounter] = React.useState(0);
 *
 *   function increment() {
 *     setCounter(counter+1)
 *   }
 *
 *   function decrement() {
 *     setCounter(counter-1)
 *   }
 *
 *   return {
 *     counter, increment, decrement
 *   }
 * }
 *
 * export const [MyViewContextProvider, useMyViewCtx] = lxContext(useMyViewContextFactory)
 * ```
 *
 * You have to wrap your components using the
 * ContextProvider generated by `lxContext`, i.e. `MyViewContextProvider`:
 *
 * ```js
 * // file: MyView.tsx
 * import { MyViewContextProvider } from "./my-view-context"
 * import { Counter, IncrementButton, DecrementButton} from "./MyButtons"
 *
 * export function MyView() {
 *   return (<>
 *     <MyViewContextProvider/>
 *      <Counter/>
 *      <IncrementButton/>
 *      <DecrementButton/>
 *     </MyViewContextProvider>
 *   <>)
 * }
 * ```
 *
 * ... and than use the context hook `useMyViewCtx()` in your components like this:
 *
 * ```js
 * // file: MyButtons.tsx
 * import { useMyViewCtx } from { "./my-view-context"}
 *
 * export function Counter() {
 *   const  { counter} = useMyViewCtx()
 *   return <>Counter: {counter}</>
 * }
 *
 * export function IncrementButton() {
 *   const  { increment } = useMyViewCtx()
 *   return <Button onClick={increment}> +1</Button>
 * }
 *
 * export function DecrementButton() {
 *   const  { decrement } = useMyViewCtx()
 *   return <Button onClick={decrement}> -1</Button>
 * }
 * ```
 *
 * ### Configuration
 *
 * In some cases you may want to configure/initialize you context,
 * for example like this:
 *
 * ```js
 * // file: my-view-context.ts
 * type MyViewContextConfig = { startValue:number }
 * const defaultConfig:MyViewContextConfig = { startValue:0 }
 *
 * function useMyViewContextFactory(config = defaultConfig) {
 *   const [counter, setCounter] = React.useState(config.startValue);
 *   ...
 * }
 *
 * // file: MyView.tsx
 * export function MyView() {
 *   return <>
 *      <MyViewContextProvider config={{ startValue: 100 }}>
 *        ...
 * }
 *
 *
 * ### Unit Tests
 *
 * To test your components you may mock your context provider
 * using the following pattern:
 *
 * // Split your component (e.g your view) into two parts:
 * // 1. A component that uses the context as "internal component"
 * // 2. A "connected" component using the provider
 *
 * ```js
 * export const MyViewInternal() {
 *  return (
 *   <>
 *    <Counter/>
 *    <IncrementButton/>
 *    <DecrementButton/>
 *   </>
 *  )
 * }
 *
 * export const MyView() {
 *  return (
 *   <MyViewContextProvider>
 *     <MyViewInternal/>
 *   </MyViewContextProvider>
 *  )
 * }
 * ```
 *
 * In you test file create a mocked context and test your view
 * using the internal component:
 *
 * ```js
 * test("MyTest", () => {
 *  // Mock your context:
 *  const mock = {
 *    counter: 100,
 *    increment: jest.fn(),
 *  };
 *
 *  const TestBed = () => (
 *    <MyViewContextProvider context={() => mock} > // 👈 The mocked context
 *      <MyViewInternal />
 *    </MyViewContextProvider>
 *  );
 *
 *  render(<TestBed />);
 *  expect(screen.getByText("Counter:100")).toBeInTheDocument();
 *  // Just check if the mock was called, the "logic" and "state"
 *  // is tested in the `useMyViewContextFactory` test
 *  fireEvent.click(screen.getByText("Increment"));
 *  expect(mock.increment).toBeCalledTimes(1);
 * });
 * ```
 *
 * Test your `useMyViewContextFactory` like an hook.
 * Here you can test the "business logic" and the "state" of your context
 *
 * ```js
 * test("useMyViewContextFactory", () => {
 *  const { result } = renderHook(() => useMyViewContextFactory());
 *  expect(result.current.counter).toBe(0);
 *  act(() => result.current.increment());
 *  expect(result.current.counter).toBe(1);
 *});
 * ```
 * @package Utils
 */
export function lxContext<T, C>(
  contextFactory: (config?: C) => T
): [
  // The return type of the created ContextProvider
  (props: {
    children?: React.ReactNode;
    config?: C;
    context?: (config?: C) => T;
  }) => JSX.Element,
  // The return type of the created useContext hook
  () => T
] {
  const Ctx = React.createContext<T>(null as unknown as T);

  function CtxProvider(props: {
    children?: React.ReactNode;
    config?: C;
    context?: (config?: C) => T; // useful for testing
  }) {
    const { context, children, config } = props;
    const ctx = context ? context(config) : contextFactory(config);
    return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
  }

  function useLxCtx() {
    const ctx = React.useContext(Ctx);
    if (!ctx) {
      throw new Error(
        `lxContext: this component should be wrapped by a ContextProvider provided by: lxContext(${contextFactory.name}). For more information see: https://daily.color.grld.eu/lxo-blocks-docs/latest/api/Utils/lxContext`
      );
    }
    return ctx;
  }

  return [CtxProvider, useLxCtx];
}
